// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
// Flight report PDF pipeline: status/button UI, the IndexedDB-backed
// per-flight report cache (open/with/read/write/cache + hydrate +
// hydrateAircraft batch), the open helpers (cached/database/either),
// manifest metadata sync + server persistence, the
// openOrGenerateFlightReport orchestrator, and the staged-download
// flow used by the toolbar Download PDF button.
//
// Exposed as a factory because the cluster mutates four download-state
// globals + several Maps/Sets in app.js, and depends on a handful of
// late-bound helpers (renderAircraftFlightsPanel, loadFlightLog,
// flightMetadataHydrationTargets, appReadOnlyMode, etc.).
//
// Identity rules (issue #480): every generation carries one immutable report
// context (flight id, analysis run id, AI summary stamp) from assembly through
// caching and saving. The context is captured from the entry the user acted
// on, cross-checked against the identity the assembler reports, and never
// re-read from the mutable selected-flight global after an await. A flight
// switch mid-generation raises FlightReportSupersededError and the request is
// dropped instead of filing flight A's bytes under flight B.

import {
  FlightReportSupersededError,
  isFlightReportSupersededError,
} from "../reporting/report-context.js";

// A cached PDF without a report id (generated before the save completed, or
// written by an older app version) is compared with the database report by
// timestamp. Client and server clocks differ by seconds, not minutes, so a
// database report this much newer than the cached copy is a different
// revision rather than the same save landing a moment later.
const CACHED_REPORT_REVISION_TOLERANCE_MS = 60_000;

export function createFlightReportPdf({
  buildFlightReportPdfBlob,
  ensureAiSummaryLoadedForPdf,
  flightReportStoreDbName,
  flightReportStoreName,
  aircraftFlightsHydrationBatchSize,
  flightReportCache,
  flightReportStoredMetadata,
  flightReportMetadataPending,
  flightReportMetadataChecked,
  flightDisplayTitle,
  appReadOnlyMode,
  readOnlyActionMessage = (action = "This action") => `${action} is disabled in demo mode.`,
  renderAircraftFlightsPanel,
  aircraftFlightPdfIconMarkup,
  flightMetadataHydrationTargets,
  loadFlightLog,
  getSelectedFlight,
  getFlightData,
  getFlightManifest,
  getReportDownloadUrl,
  setReportDownloadUrl,
  getReportDownloadBlob,
  setReportDownloadBlob,
  getReportDownloadFileName,
  setReportDownloadFileName,
  getReportDownloadFlightId,
  setReportDownloadFlightId,
}) {
  // Generations run one at a time. A second request (another history row, or
  // the toolbar button) waits for the first to finish instead of racing it
  // for the shared selected-flight state.
  let generationChain = Promise.resolve();
  // The last toolbar report whose local download succeeded but whose save to
  // the account failed. The next Download PDF click for the same flight,
  // analysis, and AI summary reuses these bytes and retries the save.
  let pendingReportSave = null;
  let reportInputRevision = 0;

  function withReportGenerationLock(task) {
    const run = generationChain.then(task, task);
    generationChain = run.then(() => undefined, () => undefined);
    return run;
  }

  async function hydrateAiSummaryForPdf() {
    if (typeof ensureAiSummaryLoadedForPdf !== "function") return;
    try {
      await ensureAiSummaryLoadedForPdf();
    } catch (error) {
      console.warn("AI summary preload for PDF failed", error);
    }
  }

  function setReportStatus(content = "") {
    const status = document.querySelector("#reportStatus");
    if (!status) return;
    status.textContent = content;
    status.hidden = !content;
  }

  function releaseReportDownloadUrl() {
    const url = getReportDownloadUrl();
    if (url?.startsWith("blob:")) {
      URL.revokeObjectURL(url);
    }
    setReportDownloadUrl(null);
    setReportDownloadBlob(null);
    setReportDownloadFileName("");
    setReportDownloadFlightId("");
  }

  function revokeCachedFlightReport(entryId) {
    const cached = flightReportCache.get(entryId);
    if (cached?.url?.startsWith("blob:")) URL.revokeObjectURL(cached.url);
    flightReportCache.delete(entryId);
  }

  function reportTimestampMs(value) {
    if (!value) return null;
    const normalized = typeof value === "string" && /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(value)
      ? `${value.replace(" ", "T")}Z`
      : value;
    const ms = Date.parse(normalized);
    return Number.isFinite(ms) ? ms : null;
  }

  function flightReportAiSummaryIsStale(entry, reportGeneratedAt) {
    const aiSummaryGeneratedAt = entry?.database?.aiSummaryGeneratedAt ?? null;
    if (!aiSummaryGeneratedAt) return false;
    const aiSummaryMs = reportTimestampMs(aiSummaryGeneratedAt);
    if (!Number.isFinite(aiSummaryMs)) return false;
    const reportMs = reportTimestampMs(reportGeneratedAt);
    return !Number.isFinite(reportMs) || reportMs < aiSummaryMs;
  }

  // A cached PDF is mismatched when it is not the same saved report revision
  // exposed by the database, or when a newer AI summary was generated after
  // the PDF. A report may still be stale relative to the flight's current
  // analysis; that is surfaced separately instead of hiding the saved PDF.
  function flightReportCacheIsStale(entry) {
    const reportAnalysisRunId = entry?.database?.reportAnalysisRunId
      ?? entry?.database?.analysisRunId
      ?? null;
    const cached = flightReportCache.get(entry?.id);
    if (!cached) return false;
    const analysisRunStale = reportAnalysisRunId
      ? (cached.analysisRunId ?? null) !== reportAnalysisRunId
      : false;
    return analysisRunStale || flightReportAiSummaryIsStale(entry, cached.generatedAt);
  }

  // True when the database holds a newer saved PDF revision than the cached
  // copy, even under the same analysis id: a regenerated report picks up new
  // notes, tests, or formatting without a new analysis run. Compared by report
  // id when the cached record carries one, else by generation timestamp.
  function cachedFlightReportIsOutdated(entry) {
    const cached = flightReportCache.get(entry?.id);
    const databaseReportId = entry?.database?.reportId ?? null;
    if (!cached || !databaseReportId) return false;
    if (cached.reportId) return cached.reportId !== databaseReportId;
    const databaseMs = reportTimestampMs(entry?.database?.reportGeneratedAt);
    const cachedMs = reportTimestampMs(cached.generatedAt);
    if (!Number.isFinite(databaseMs) || !Number.isFinite(cachedMs)) return false;
    return databaseMs - cachedMs > CACHED_REPORT_REVISION_TOLERANCE_MS;
  }

  function flightReportAvailable(entry) {
    const cached = flightReportCache.get(entry?.id);
    if (cached) return true;
    if (entry?.database?.reportId) return true;
    const stored = flightReportStoredMetadata.get(entry?.id);
    return Boolean(stored);
  }

  function flightReportStale(entry) {
    const derivedStatus = entry?.database?.compatibilityStatus?.derivedArtifacts?.report?.status;
    return derivedStatus === "stale"
      || flightReportCacheIsStale(entry)
      || flightReportAiSummaryIsStale(entry, entry?.database?.reportGeneratedAt);
  }

  function flightReportMetadataKey(entry) {
    return entry?.database?.flightId ?? entry?.id ?? "";
  }

  function openFlightReportStore() {
    if (!globalThis.indexedDB) return Promise.resolve(null);
    return new Promise((resolve) => {
      const request = indexedDB.open(flightReportStoreDbName, 1);
      request.addEventListener("upgradeneeded", () => {
        request.result.createObjectStore(flightReportStoreName, { keyPath: "flightId" });
      });
      request.addEventListener("success", () => resolve(request.result));
      request.addEventListener("error", () => resolve(null));
      request.addEventListener("blocked", () => resolve(null));
    });
  }

  async function withFlightReportStore(mode, callback) {
    const database = await openFlightReportStore();
    if (!database) return null;
    return new Promise((resolve) => {
      const transaction = database.transaction(flightReportStoreName, mode);
      const store = transaction.objectStore(flightReportStoreName);
      const done = (value) => {
        database.close();
        resolve(value);
      };
      transaction.addEventListener("error", () => done(null));
      transaction.addEventListener("abort", () => done(null));
      callback(store, done);
    });
  }

  async function readStoredFlightReportRecord(flightId) {
    if (!flightId) return null;
    return withFlightReportStore("readonly", (store, done) => {
      const request = store.get(flightId);
      request.addEventListener("success", () => done(request.result ?? null));
      request.addEventListener("error", () => done(null));
    });
  }

  // `identity` carries the report's provenance: { analysisRunId, reportId,
  // generatedAt }. A bare string is accepted as the analysis run id for
  // callers written against the older positional signature.
  async function writeStoredFlightReport(flightId, blob, fileName, identity = null) {
    if (!flightId || !blob?.size) return null;
    const options = identity && typeof identity === "object" ? identity : { analysisRunId: identity ?? null };
    const generatedAt = options.generatedAt ?? new Date().toISOString();
    const record = {
      flightId,
      blob,
      fileName: fileName || "engine-report.pdf",
      generatedAt,
      byteSize: blob.size,
      analysisRunId: options.analysisRunId ?? null,
      reportId: options.reportId ?? null,
    };
    const saved = await withFlightReportStore("readwrite", (store, done) => {
      const request = store.put(record);
      request.addEventListener("success", () => done(record));
      request.addEventListener("error", () => done(null));
    });
    if (saved) {
      flightReportStoredMetadata.set(flightId, {
        fileName: saved.fileName,
        generatedAt: saved.generatedAt,
        byteSize: saved.byteSize,
      });
    }
    return saved;
  }

  function cacheStoredFlightReport(record) {
    if (!record?.flightId || !record?.blob?.size) return false;
    revokeCachedFlightReport(record.flightId);
    flightReportCache.set(record.flightId, {
      blob: record.blob,
      fileName: record.fileName || "engine-report.pdf",
      url: URL.createObjectURL(record.blob),
      generatedAt: record.generatedAt,
      analysisRunId: record.analysisRunId ?? null,
      reportId: record.reportId ?? null,
    });
    flightReportStoredMetadata.set(record.flightId, {
      fileName: record.fileName || "engine-report.pdf",
      generatedAt: record.generatedAt ?? null,
      byteSize: record.byteSize ?? record.blob.size,
    });
    return true;
  }

  async function hydrateStoredFlightReportMetadata(entry) {
    if (!entry?.id || flightReportCache.has(entry.id) || flightReportStoredMetadata.has(entry.id)) return false;
    const record = await readStoredFlightReportRecord(entry.id);
    if (!record?.blob?.size) return false;
    return cacheStoredFlightReport(record);
  }

  async function hydrateAircraftFlightReportMetadata(entries = [], aircraft = "") {
    const { storeTargets, databaseTargets } = flightMetadataHydrationTargets(entries);
    if (!storeTargets.length && !databaseTargets.length) return;

    let changed = false;
    storeTargets.forEach((entry) => flightReportMetadataPending.add(`store:${entry.id}`));
    databaseTargets.forEach((entry) => flightReportMetadataPending.add(`db:${flightReportMetadataKey(entry)}`));
    for (let index = 0; index < storeTargets.length; index += aircraftFlightsHydrationBatchSize) {
      const chunk = storeTargets.slice(index, index + aircraftFlightsHydrationBatchSize);
      await Promise.all(chunk.map(async (entry) => {
        const key = `store:${entry.id}`;
        try {
          const found = await hydrateStoredFlightReportMetadata(entry);
          if (found) changed = true;
          else flightReportMetadataChecked.add(key);
        } finally {
          flightReportMetadataPending.delete(key);
        }
      }));
    }

    for (let index = 0; index < databaseTargets.length; index += aircraftFlightsHydrationBatchSize) {
      const chunk = databaseTargets.slice(index, index + aircraftFlightsHydrationBatchSize);
      await Promise.all(chunk.map(async (entry) => {
        const key = flightReportMetadataKey(entry);
        const pendingKey = `db:${key}`;
        try {
          const response = await fetch(`/api/flights/${encodeURIComponent(entry.id)}/report?metadata=1`, { cache: "no-store" });
          if (response.status === 404) {
            flightReportMetadataChecked.add(pendingKey);
            return;
          }
          if (!response.ok) return;
          const payload = await response.json().catch(() => ({}));
          if (payload?.report?.id) {
            updateManifestReportMetadata(entry, payload.report);
            flightReportMetadataChecked.delete(pendingKey);
            changed = true;
          } else {
            flightReportMetadataChecked.add(pendingKey);
          }
        } catch {
          // Leave transient failures unchecked so a later render can try again.
        } finally {
          flightReportMetadataPending.delete(pendingKey);
        }
      }));
    }

    if (changed) renderAircraftFlightsPanel(aircraft);
  }

  function updateReportButtonState(isCompare = false) {
    const button = document.querySelector("#action-report");
    if (!button) return;
    const label = button.querySelector(".button-label");
    const text = isCompare ? "Export Comparison" : "Download PDF";
    if (label) label.textContent = text;
    else button.textContent = text;
  }

  function resetGeneratedReport() {
    reportInputRevision += 1;
    pendingReportSave = null;
    releaseReportDownloadUrl();
    setReportStatus("");
    updateReportButtonState();
  }

  // Report identity for a generation: the entry the user acted on plus the
  // provenance the assembler captured. `report` is the assembler result; when
  // it names a flight that differs from `entry`, the app switched flights
  // underneath the generation and the bytes must not be attributed to either.
  function reportContextFor(entry, report) {
    const entryId = entry?.id ?? null;
    const reportFlightId = report?.flightId ?? null;
    if (entryId && reportFlightId && String(reportFlightId) !== String(entryId)) {
      throw new FlightReportSupersededError(
        `The report was generated for a different flight (${reportFlightId}) than the one requested (${entryId}).`,
      );
    }
    return {
      flightId: String(entryId ?? reportFlightId ?? "sample"),
      analysisRunId: report?.analysisRunId ?? entry?.database?.analysisRunId ?? null,
      aiSummaryGeneratedAt: report?.aiSummaryGeneratedAt ?? null,
      aircraft: entry?.aircraft ?? report?.aircraft ?? "",
      generatedAt: report?.generatedAt ?? new Date().toISOString(),
    };
  }

  function assertEntryStillSelected(entry) {
    if (!entry?.id) return;
    const selected = getSelectedFlight();
    if (selected === entry) return;
    if (String(selected?.id ?? "") !== String(entry.id)) {
      throw new FlightReportSupersededError(
        `The selected flight changed before the report for ${flightDisplayTitle(entry)} could be generated.`,
      );
    }
  }

  // Stage a generated PDF for download and cache it under the report's own
  // flight identity. `context` comes from reportContextFor(); the selected
  // flight is deliberately not consulted here.
  async function stageReportDownload(blob, fileName, context = null) {
    releaseReportDownloadUrl();
    if (!blob?.size) {
      throw new Error("Generated report was empty.");
    }
    const resolvedContext = context ?? reportContextFor(getSelectedFlight(), null);
    const url = URL.createObjectURL(blob);
    const cachedUrl = URL.createObjectURL(blob);
    const generatedAt = resolvedContext.generatedAt ?? new Date().toISOString();
    setReportDownloadUrl(url);
    setReportDownloadBlob(blob);
    setReportDownloadFileName(fileName);
    const flightId = resolvedContext.flightId;
    setReportDownloadFlightId(flightId);
    if (flightId) {
      const analysisRunId = resolvedContext.analysisRunId ?? null;
      revokeCachedFlightReport(flightId);
      flightReportCache.set(flightId, {
        blob,
        fileName,
        url: cachedUrl,
        generatedAt,
        analysisRunId,
        reportId: null,
      });
      await writeStoredFlightReport(flightId, blob, fileName, { analysisRunId, generatedAt });
      renderAircraftFlightsPanel(resolvedContext.aircraft);
    }
    setReportStatus(`Report ready: ${fileName}`);
  }

  function clickDownloadLink(url, fileName) {
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName || "engine-report.pdf";
    link.rel = "noopener";
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    link.remove();
  }

  function downloadReportBlob(blob, fileName) {
    if (!blob?.size || !fileName) throw new Error("Generated report was empty.");
    const url = URL.createObjectURL(blob);
    try {
      clickDownloadLink(url, fileName);
      setReportStatus(`Download started: ${fileName}`);
    } finally {
      // Give the browser time to consume this URL independently of navigation.
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    }
  }

  // One deliberate opening mechanism. The blob URL is same-origin, so
  // window.open returns the new window when it opens and null only when the
  // browser blocked it; "noopener" is not passed because it forces a null
  // return even on success, which used to trigger the download fallback on
  // top of the tab. Opener isolation is preserved by severing the opener on
  // the returned window instead.
  function openReportUrl(cached) {
    if (!cached?.url) return false;
    const opened = window.open(cached.url, "_blank");
    if (opened) {
      try {
        opened.opener = null;
      } catch {
        // Cross-origin openers throw; a same-origin blob tab never does.
      }
      return true;
    }
    clickDownloadLink(cached.url, cached.fileName);
    return true;
  }

  function openCachedFlightReport(entryId) {
    return openReportUrl(flightReportCache.get(entryId));
  }

  // The report endpoint is JWT-gated (Authorization: Bearer ...). A top-level
  // navigation, window.open() or an <a download> click, cannot carry that
  // header (only the patched window.fetch injects it), so navigating straight
  // to /api/flights/<id>/report 401s and shows no PDF. Instead, fetch the bytes
  // through fetch (which is auth-aware), cache them as a same-origin blob, and
  // open that. Returns false on any failure so callers fall back to the cache.
  async function openDatabaseFlightReport(entry) {
    if (!entry?.database?.reportId) return false;
    try {
      const response = await fetch(`/api/flights/${encodeURIComponent(entry.id)}/report`, { cache: "no-store" });
      if (!response.ok) return false;
      const blob = new Blob([await response.arrayBuffer()], { type: "application/pdf" });
      if (!blob.size) return false;
      const metadata = response.headers?.get("x-report-metadata");
      if (!metadata) {
        // Older servers cannot identify the served revision. Deliver the file
        // without caching unverified bytes as the manifest's latest report.
        const url = URL.createObjectURL(blob);
        try {
          return openReportUrl({ url, fileName: "engine-report.pdf" });
        } finally {
          window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
        }
      }
      const report = JSON.parse(decodeURIComponent(metadata));
      if (!report?.id || String(report.flight_id) !== String(entry.id)) return false;
      const record = {
        flightId: entry.id,
        blob,
        fileName: report.file_name || "engine-report.pdf",
        generatedAt: report.generated_at ?? null,
        analysisRunId: report.analysis_run_id ?? null,
        reportId: report.id,
      };
      cacheStoredFlightReport(record);
      await writeStoredFlightReport(entry.id, blob, record.fileName, {
        analysisRunId: record.analysisRunId,
        reportId: record.reportId,
        generatedAt: record.generatedAt ?? undefined,
      });
      return openCachedFlightReport(entry.id);
    } catch (error) {
      console.warn("Database flight report fetch failed", error);
      return false;
    }
  }

  async function openExistingFlightReport(entry) {
    // Prefer the in-memory/IndexedDB blob (no network, no auth) when it is the
    // same revision the database reports. An older cached copy is replaced by
    // the saved revision instead of being presented as the latest PDF.
    if (cachedFlightReportIsOutdated(entry)) {
      return openDatabaseFlightReport(entry);
    }
    if (openCachedFlightReport(entry?.id)) return true;
    return openDatabaseFlightReport(entry);
  }

  function blobToBase64(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.addEventListener("load", () => {
        const result = String(reader.result ?? "");
        resolve(result.includes(",") ? result.split(",").pop() : result);
      });
      reader.addEventListener("error", () => reject(reader.error ?? new Error("Could not read generated PDF.")));
      reader.readAsDataURL(blob);
    });
  }

  function updateManifestReportMetadata(entry, report) {
    if (!entry?.database || !report?.id) return;
    const updates = {
      reportId: report.id,
      reportAnalysisRunId: report.analysis_run_id ?? entry.database.reportAnalysisRunId ?? null,
      reportFileName: report.file_name ?? entry.database.reportFileName ?? null,
      reportGeneratedAt: report.generated_at ?? entry.database.reportGeneratedAt ?? null,
      reportByteSize: report.byte_size ?? entry.database.reportByteSize ?? null,
    };
    Object.assign(entry.database, updates);
    const manifestEntry = getFlightManifest().find((item) => item.id === entry.id);
    if (manifestEntry?.database) Object.assign(manifestEntry.database, updates);
  }

  // Save the generated bytes under the analysis that produced them. The
  // server validates that `analysisRunId` belongs to the flight and refuses
  // to relabel the bytes as a newer run.
  async function persistDatabaseFlightReport(entry, blob, fileName, context = null) {
    if (!entry?.database?.flightId || !blob?.size) return null;
    if (appReadOnlyMode()) return null;
    const analysisRunId = context?.analysisRunId ?? entry.database.analysisRunId ?? null;
    const response = await fetch(`/api/flights/${encodeURIComponent(entry.id)}/report`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        fileName,
        pdfBase64: await blobToBase64(blob),
        analysisRunId,
      }),
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload?.error ?? `Report persistence API HTTP ${response.status}`);
    }
    updateManifestReportMetadata(entry, payload.report);
    const cached = flightReportCache.get(entry.id);
    if (cached && cached.blob === blob && payload.report?.id) {
      cached.reportId = payload.report.id;
      await writeStoredFlightReport(entry.id, blob, fileName, {
        analysisRunId: cached.analysisRunId ?? analysisRunId,
        reportId: payload.report.id,
        generatedAt: cached.generatedAt,
      });
    }
    renderAircraftFlightsPanel(entry.aircraft);
    return payload.report;
  }

  // Build + stage a PDF for `entry` under one frozen identity. Throws
  // FlightReportSupersededError when the app no longer shows that flight at
  // any of the await boundaries (load, AI hydrate, assembly).
  async function generateFlightReportForEntry(entry, { load = true } = {}) {
    if (load) await loadFlightLog(entry, { activateTrial: true });
    assertEntryStillSelected(entry);
    await hydrateAiSummaryForPdf();
    assertEntryStillSelected(entry);
    const report = await buildFlightReportPdfBlob();
    const context = reportContextFor(entry, report);
    await stageReportDownload(report.blob, report.fileName, context);
    return { blob: report.blob, fileName: report.fileName, context };
  }

  function reportSaveFailureMessage(fileName, error) {
    return `The PDF report ${fileName ? `"${fileName}" ` : ""}was generated, but saving a copy to your account failed: ${error?.message ?? error}`;
  }

  async function openOrGenerateFlightReport(entry, button = null) {
    if (!entry?.id) return;
    if (await openExistingFlightReport(entry)) return;
    if (appReadOnlyMode()) {
      window.alert(readOnlyActionMessage("Generating PDF reports"));
      renderAircraftFlightsPanel(entry.aircraft);
      return;
    }
    const originalText = button?.textContent ?? "";
    const originalMarkup = button?.innerHTML ?? "";
    const originalAriaLabel = button?.getAttribute("aria-label") ?? "";
    const iconOnlyButton = button?.classList?.contains("is-icon-only");
    try {
      if (button) {
        button.disabled = true;
        button.classList.add("is-generating");
        button.setAttribute("aria-label", "Generating PDF report");
        if (!iconOnlyButton) button.textContent = "Generating";
      }
      await withReportGenerationLock(async () => {
        const { blob, fileName, context } = await generateFlightReportForEntry(entry);
        let saveError = null;
        try {
          await persistDatabaseFlightReport(entry, blob, fileName, context);
        } catch (error) {
          saveError = error;
          console.error("Flight history report save failed", error);
        }
        openCachedFlightReport(entry.id);
        if (saveError) window.alert(reportSaveFailureMessage(fileName, saveError));
      });
    } catch (error) {
      if (isFlightReportSupersededError(error)) {
        console.info("Flight history report skipped: selected flight changed", error.message);
      } else {
        console.error("Flight history report failed", error);
        window.alert(`Could not generate report: ${error.message}`);
      }
    } finally {
      if (button) {
        button.disabled = false;
        button.classList.remove("is-generating");
        if (flightReportAvailable(entry)) {
          button.innerHTML = aircraftFlightPdfIconMarkup();
          button.setAttribute("aria-label", "Open PDF report");
        } else if (iconOnlyButton) {
          button.innerHTML = originalMarkup || aircraftFlightPdfIconMarkup();
          button.setAttribute("aria-label", originalAriaLabel || "Generate PDF");
        } else {
          button.textContent = originalText || "Generate PDF";
          if (originalAriaLabel) button.setAttribute("aria-label", originalAriaLabel);
        }
      }
      renderAircraftFlightsPanel(entry.aircraft);
    }
  }

  async function regenerateFlightReport(entry, button = null) {
    if (!entry?.id) return;
    if (appReadOnlyMode()) {
      window.alert(readOnlyActionMessage("Regenerating PDF reports"));
      renderAircraftFlightsPanel(entry.aircraft);
      return;
    }
    try {
      if (button) {
        button.disabled = true;
        button.classList.add("is-generating");
        button.setAttribute("aria-label", `Regenerating PDF report for ${flightDisplayTitle(entry)}`);
      }
      await withReportGenerationLock(async () => {
        const { blob, fileName, context } = await generateFlightReportForEntry(entry);
        let saveError = null;
        try {
          await persistDatabaseFlightReport(entry, blob, fileName, context);
        } catch (error) {
          saveError = error;
          console.error("Flight history report regeneration save failed", error);
        }
        openCachedFlightReport(entry.id);
        if (saveError) window.alert(reportSaveFailureMessage(fileName, saveError));
      });
    } catch (error) {
      if (isFlightReportSupersededError(error)) {
        console.info("Flight history report regeneration skipped: selected flight changed", error.message);
      } else {
        console.error("Flight history report regeneration failed", error);
        window.alert(`Could not regenerate report: ${error.message}`);
      }
    } finally {
      if (button) {
        button.disabled = false;
        button.classList.remove("is-generating");
      }
      renderAircraftFlightsPanel(entry.aircraft);
    }
  }

  function triggerPreparedReportDownload() {
    const url = getReportDownloadUrl();
    const fileName = getReportDownloadFileName();
    const blob = getReportDownloadBlob();
    if (!url || !fileName) return false;
    if (!blob?.size) return false;
    clickDownloadLink(url, fileName);
    setReportStatus(`Download started: ${fileName}`);
    return true;
  }

  function waitForButtonPaint() {
    return new Promise((resolve) => {
      requestAnimationFrame(() => requestAnimationFrame(resolve));
    });
  }

  function waitForMinimumReportFeedback() {
    return new Promise((resolve) => {
      window.setTimeout(resolve, 2000);
    });
  }

  // A previously generated toolbar PDF whose account save failed can be
  // retried without regenerating, as long as nothing that feeds the report
  // has changed: same flight entry, same loaded samples, same analysis, and
  // the same AI summary stamp.
  function reusablePendingReport(entry) {
    const pending = pendingReportSave;
    if (!pending) return null;
    const sameEntry = pending.entryId === (entry?.id ?? null);
    const sameData = pending.flightData === getFlightData();
    const sameAnalysis = (pending.context?.analysisRunId ?? null) === (entry?.database?.analysisRunId ?? null);
    const sameSummary = (pending.aiSummaryGeneratedAt ?? null) === (entry?.database?.aiSummaryGeneratedAt ?? null);
    if (sameEntry && sameData && sameAnalysis && sameSummary
      && pending.inputRevision === reportInputRevision && pending.blob?.size) return pending;
    pendingReportSave = null;
    return null;
  }

  function pendingReportSaveState() {
    return pendingReportSave ? { entryId: pendingReportSave.entryId, fileName: pendingReportSave.fileName } : null;
  }

  async function downloadFlightReportPdf() {
    if (!getFlightData().length) {
      window.alert("Load a flight log before downloading a report.");
      return;
    }
    const entry = getSelectedFlight();
    const button = document.querySelector("#action-report");
    try {
      if (button) {
        button.disabled = true;
        button.classList.add("is-generating");
        const label = button.querySelector(".button-label");
        if (label) label.textContent = "Generating PDF";
        else button.textContent = "Generating PDF";
      }
      setReportStatus("Generating PDF...");
      await waitForButtonPaint();
      await withReportGenerationLock(async () => {
        const minimumFeedback = waitForMinimumReportFeedback();
        let staged = reusablePendingReport(entry);
        if (staged) {
          await stageReportDownload(staged.blob, staged.fileName, staged.context);
        } else {
          await hydrateAiSummaryForPdf();
          assertEntryStillSelected(entry);
          const retryInputs = {
            inputRevision: reportInputRevision,
            flightData: getFlightData(),
            aiSummaryGeneratedAt: entry?.database?.aiSummaryGeneratedAt ?? null,
          };
          const report = await buildFlightReportPdfBlob();
          const context = reportContextFor(entry, report);
          await stageReportDownload(report.blob, report.fileName, context);
          staged = { ...retryInputs, blob: report.blob, fileName: report.fileName, context };
        }
        await minimumFeedback;
        // Local export first: a valid PDF reaches the member even when the
        // account save below fails.
        downloadReportBlob(staged.blob, staged.fileName);
        try {
          await persistDatabaseFlightReport(entry, staged.blob, staged.fileName, staged.context);
          pendingReportSave = null;
        } catch (error) {
          console.error("Report save failed after download", error);
          pendingReportSave = staged.inputRevision === reportInputRevision ? {
            ...staged,
            entryId: entry?.id ?? null,
          } : null;
          const message = `Downloaded ${staged.fileName}, but saving a copy to your account failed: ${error.message}. Use Download PDF again to retry saving.`;
          setReportStatus(message);
          window.alert(message);
        }
      });
    } catch (error) {
      if (isFlightReportSupersededError(error)) {
        const message = "Report cancelled: the selected flight changed while it was being generated.";
        console.info(message, error.message);
        setReportStatus(message);
      } else {
        console.error("Report download failed", error);
        const message = `Could not generate report: ${error.message}`;
        setReportStatus(message);
        window.alert(message);
      }
    } finally {
      if (button) {
        button.disabled = false;
        button.classList.remove("is-generating");
        updateReportButtonState();
      }
    }
  }

  return {
    setReportStatus,
    releaseReportDownloadUrl,
    revokeCachedFlightReport,
    flightReportAvailable,
    flightReportStale,
    cachedFlightReportIsOutdated,
    flightReportMetadataKey,
    openFlightReportStore,
    withFlightReportStore,
    readStoredFlightReportRecord,
    writeStoredFlightReport,
    cacheStoredFlightReport,
    hydrateStoredFlightReportMetadata,
    hydrateAircraftFlightReportMetadata,
    updateReportButtonState,
    resetGeneratedReport,
    stageReportDownload,
    openCachedFlightReport,
    openDatabaseFlightReport,
    openExistingFlightReport,
    blobToBase64,
    updateManifestReportMetadata,
    persistDatabaseFlightReport,
    generateFlightReportForEntry,
    openOrGenerateFlightReport,
    regenerateFlightReport,
    triggerPreparedReportDownload,
    waitForButtonPaint,
    waitForMinimumReportFeedback,
    pendingReportSaveState,
    downloadFlightReportPdf,
  };
}
